import React, { useState } from 'react';
import { formatPrice } from 'utils';
import { ButtonCommon } from '../Button/ButtonCommon';
import { Eye, GlobeHemisphereEast, Info, PaperPlaneTilt, Plus, Printer, X } from '@phosphor-icons/react';
import { SaleOrder, SaleOrderStatusCode } from 'models/SaleOrder';
import { PaymentType } from 'models';
import QuickViewTransactionsModal from '../Modal/QuickViewTransactionsModal';
import { DebtDetailModal } from 'components/Debt';
import { useCreateSaleOrderBillMutation, useGetSaleOrderInvoiceExistenceQuery } from 'services/sale.service';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUpdateCancelSaleOrderMutation } from 'services/cancelSaleOrder.service';
import ConfirmationModal from '../Modal/ConfirmationModal';
import { showAlertNotification } from '../Modal/AlertNotification';
import { ETransType } from 'models/Transaction';
import AddExtraFeeModal from '../Modal/AddExtraFeeModal';
import envService, { HiddenFeatures } from 'services/env.service';

interface SaleOrderDetailPaymentProps {
  saleOrder: SaleOrder;
}

const SaleOrderDetailPayment: React.FC<SaleOrderDetailPaymentProps> = ({ saleOrder }) => {
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isModalOpenCancel, setIsModalOpenCancel] = React.useState(false);
  const [searchParams] = useSearchParams();
  const saleOrderId = searchParams.get('id') || '';
  const navigate = useNavigate();

  const [updateCancelSaleOrder] = useUpdateCancelSaleOrderMutation();
  const [isExtraFeeModalOpen, setIsExtraFeeModalOpen] = useState(false);
  const [createSaleOrderBill, { isLoading }] = useCreateSaleOrderBillMutation();
  const rawTotal =
    saleOrder?.saleOrderItems?.reduce((total, item) => {
      return total + item.salePrice * item.quantity;
    }, 0) || 0;
  const { data: invoiceExistence } = useGetSaleOrderInvoiceExistenceQuery(saleOrder?.id || '', {
    skip: !saleOrder?.id,
  });
  const vatValue = saleOrder.vat ? (rawTotal * saleOrder.vat) / 100 : 0;
  // Calculate VAT total (raw total + VAT)
  const vatTotal = rawTotal + vatValue;

  // Calculate member discount
  const memberDiscountValue = (vatTotal * (saleOrder.memberDiscount || 0)) / 100;

  // Calculate regular discount
  const regularDiscountValue =
    saleOrder.discountUnit === 'PERCENT' ? (vatTotal * (saleOrder.discount || 0)) / 100 : saleOrder.discount || 0;

  const handleViewDebt = () => {
    if (saleOrder?.Debt?.length) {
      setIsDetailModalOpen(true);
    } else {
      console.warn('No debt information available');
    }
  };
  const depositAmount =
    saleOrder.Transactions?.reduce(
      (total, transaction) =>
        total + (transaction.type === ETransType.Deposit ? Number(transaction.transactionAmount) : 0),
      0,
    ) || 0;
  const extraFeeAmount =
    saleOrder.Transactions?.reduce(
      (total, transaction) =>
        total + (transaction.type === ETransType.OS_EXTRA_FEE ? Number(transaction.transactionAmount) : 0),
      0,
    ) || 0;
  console.log(saleOrder);
  const paidAmount =
    saleOrder?.paymentType === PaymentType.FULL_PAYMENT
      ? saleOrder?.total || 0
      : saleOrder?.status?.code === SaleOrderStatusCode.DRAFT
        ? saleOrder?.deposit || 0
        : depositAmount;

  const handlePrintBills = async (orderId: string) => {
    try {
      const response = await createSaleOrderBill({ id: orderId });
      const billUrl = response?.data?.data?.fileUrl;
      if (!billUrl) return;

      const res = await fetch(billUrl);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);

      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = blobUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      };
    } catch (error) {
      console.log(error);
      if ((error as Error)?.message) {
        alert((error as Error)?.message);
      } else {
        alert('Lỗi in hóa đơn');
      }
    }
  };

  const handleExportInvoice = async () => {
    try {
      if (saleOrder) {
        navigate(`/invoices/electronic-invoices/create-invoice`, {
          state: { saleOrder: saleOrder },
        });
      }
      // const invoiceResponses = response?.data?.data?.providerInvoice?.data;
      // // const { errorCode = 200, message } = response?.data?.data?.providerInvoice || {};
      // const billUrl = invoiceResponses?.[0]?.data?.urlDownloadPDF;
      // if (!billUrl) return;

      // invoiceResponses?.forEach((invoiceResponse) => {
      //   const { errorCode = 200, message } = invoiceResponse;
      //   if (![200, 201].includes(errorCode)) {
      //     showAlertNotification({
      //       type: 'warning',
      //       title: 'Cảnh báo!',
      //       message: message ? message : 'Bạn đã xuất hoá đơn điện tử cho đơn hàng này rồi!',
      //     });
      //   }
      // });
      // const a = document.createElement('a');
      // a.href = billUrl;
      // a.rel = 'noopener';
      // document.body.appendChild(a);
      // a.click();
      // document.body.removeChild(a);
      // window.open(billUrl, '_blank', 'noopener');
    } catch (error) {
      console.log(error);
      if ((error as Error)?.message) {
        alert((error as Error)?.message);
      } else {
        alert('Lỗi xuất hóa đơn điện tử');
      }
    }
  };
  return (
    <div className='bg-white rounded-lg p-6 h-full overflow-y-auto'>
      <div className='text-lg font-bold text-[var(--textBlack100)] mb-4'>THÔNG TIN THANH TOÁN</div>

      <div className='grid grid-cols-4 gap-4 gap-y-4 items-center'>
        <div className='text-sm text-[var(--textBlack100)]'>Số lượng</div>
        <div className='text-sm font-medium flex items-center gap-2 col-span-3'>
          <span className='flex gap-2 text-[var(--textBlack56)]'>
            <span className='font-medium text-[var(--textBlack100)]'>
              {saleOrder?.saleOrderItems?.reduce((total, item) => total + item.quantity, 0)}
            </span>{' '}
            sản phẩm
          </span>
        </div>

        <span className='text-sm text-[var(--textBlack100)]'>Tạm tính</span>
        <span className='col-span-3 font-bold text-[var(--textBlack100)]'>
          {formatPrice(rawTotal)} <span className='text-[var(--textBlack56)]'>VND</span>
        </span>

        <hr className='my-2 col-span-4' />

        <span className='text-sm text-[var(--textBlack100)]'>VAT</span>
        <span className='col-span-3 font-medium text-[var(--textBlack100)]   flex items-center'>
          {saleOrder?.vat || 0}% <span className='text-[var(--textBlack56)] mx-2'>~</span> {formatPrice(vatValue)}
          <span className='ml-2 text-[var(--textBlack56)]'> VND</span>
        </span>
        <span className='text-sm text-[var(--textBlack100)]'>Giảm giá hạng thẻ</span>
        <span className='col-span-3 font-medium text-[var(--textBlack100)]   flex items-center'>
          {saleOrder?.memberDiscount || 0}% <span className='text-[var(--textBlack56)] mx-2'>~</span>{' '}
          {formatPrice(memberDiscountValue)}
          <span className='ml-2 text-[var(--textBlack56)]'> VND</span>
        </span>

        <span className='text-sm text-[var(--textBlack100)]'>Giảm giá</span>
        <span className='col-span-3 font-medium text-[var(--textBlack100)]   flex items-center'>
          {saleOrder?.discountUnit === 'PERCENT' && (
            <>
              {saleOrder?.discount || 0}% <span className='text-[var(--textBlack56)] mx-2'>~</span>{' '}
            </>
          )}
          {formatPrice(regularDiscountValue)}
          <span className='ml-2 text-[var(--textBlack56)]'> VND</span>
        </span>
        <span className='text-sm text-[var(--textBlack100)]'>Phụ phí</span>
        <span className='col-span-3 font-medium text-[var(--textBlack100)]   flex items-center justify-between'>
          <span>
            {formatPrice(extraFeeAmount)}
            <span className='ml-2 text-[var(--textBlack56)]'> VND</span>
          </span>
          <ButtonCommon
            size='xxs'
            color='primary'
            radius='rounded'
            onClick={() => setIsExtraFeeModalOpen(true)}
            className='!px-2'
            icon={<Plus size={14} />}
          >
            Thêm phụ phí
          </ButtonCommon>
        </span>
        <hr className='my-2 col-span-4' />

        <span className='text-sm font-bold text-[var(--textBlack100)]'>Tổng tiền</span>
        <span className='col-span-3 font-bold text-[var(--textBlack100)]'>
          {formatPrice(saleOrder?.total || 0)} <span className='text-[var(--textBlack56)]'>VND</span>
        </span>

        <span className='text-sm font-bold text-[var(--textBlack100)]'>Đã thanh toán</span>
        <span className='col-span-3 font-bold text-[var(--primaryColor500)]'>
          {saleOrder?.paymentType === PaymentType.FULL_PAYMENT
            ? formatPrice(saleOrder?.total || 0)
            : formatPrice(paidAmount || 0)}{' '}
          <span className='opacity-60'>VND</span>
        </span>

        <span className='text-sm text-[var(--textBlack56)]'>Còn lại</span>
        <span className='col-span-3 font-bold text-[var(--textBlack100)]'>
          {saleOrder?.paymentType === PaymentType.FULL_PAYMENT
            ? formatPrice(0)
            : formatPrice(Number(saleOrder?.total || 0) - Number(paidAmount || 0))}{' '}
          <span className='text-[var(--textBlack56)]'>VND</span>
        </span>

        {/* {transactions.length > 0 && ( */}
        <div className='col-span-4 flex items-center gap-2 mt-2'>
          <div
            className='text-sm text-[var(--primaryColor500)] font-bold cursor-pointer'
            onClick={() => setIsTransactionModalOpen(true)}
          >
            Lịch sử thanh toán
          </div>
        </div>
        {/* )} */}
      </div>

      <div className='flex flex-col gap-2 mt-6'>
        {saleOrder?.paymentType !== PaymentType.FULL_PAYMENT &&
          saleOrder?.status?.code !== 'so-cancel' &&
          saleOrder?.status?.code !== 'so-draft' && (
            <ButtonCommon
              color='dangerSoft'
              radius='rounded'
              icon={<Info size={20} />}
              onClick={handleViewDebt}
              className='w-full'
            >
              Chi tiết công nợ
            </ButtonCommon>
          )}

        {saleOrder?.status?.code !== 'so-draft' && (
          <ButtonCommon
            color='soft'
            radius='rounded'
            icon={<Printer size={20} />}
            onClick={() => handlePrintBills(saleOrder?.id || '')}
            className='w-full'
            loading={isLoading}
          >
            In hóa đơn
          </ButtonCommon>
        )}

        {!envService.isFeatureHidden(HiddenFeatures.EInvoice) &&
          saleOrder?.status?.code !== 'so-draft' &&
          saleOrder?.status?.code !== 'so-cancel' &&
          !invoiceExistence?.data?.exists && (
            <ButtonCommon
              color='soft'
              radius='rounded'
              icon={<PaperPlaneTilt size={20} />}
              onClick={() => handleExportInvoice()}
              className='w-full'
            >
              Tạo hoá đơn điện tử
            </ButtonCommon>
          )}
        {!envService.isFeatureHidden(HiddenFeatures.EInvoice) &&
          saleOrder?.status?.code !== 'so-draft' &&
          saleOrder?.status?.code !== 'so-cancel' &&
          invoiceExistence?.data?.exists && (
            <ButtonCommon
              color='soft'
              radius='rounded'
              icon={<Eye size={20} />}
              onClick={() => {
                const invoiceDetailUrl = `/invoices/electronic-invoices/view/${invoiceExistence?.data?.invoice.id}`;
                window.open(invoiceDetailUrl, '_blank', 'noopener,noreferrer');
              }}
              className='w-full'
            >
              Xem chi tiết đơn điện tử
            </ButtonCommon>
          )}
        {saleOrder?.status?.code === 'so-draft' && (
          <ButtonCommon
            color='dangerSoft'
            onClick={() => {
              navigate(`/create-sale-order?copyId=${saleOrder.id}&type=edit&seed=${Date.now()}`);
            }}
            radius='rounded'
            icon={<GlobeHemisphereEast size={20} />}
          >
            Tạo đơn hàng
          </ButtonCommon>
        )}
        {/* {
          saleOrder?.status?.code === "so-draft" && (
            <ButtonCommon
              color='soft'
              onClick={ () => {
                navigate(`/create-sale-order?copyId=${saleOrder.id}&type=edit&seed=${Date.now()}`);
              }}
              radius='rounded'
              icon={<Pen size={20} />}
            >
              Chỉnh sửa đơn hàng
            </ButtonCommon>
          )
        } */}
        {saleOrder?.status?.code !== 'so-cancel' && (
          <ButtonCommon
            color='danger'
            onClick={() => {
              setIsModalOpenCancel(true);
            }}
            radius='rounded'
            icon={<X size={20} />}
          >
            Hủy đơn hàng
          </ButtonCommon>
        )}
      </div>

      {/* Transaction history modal */}
      <DebtDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        debtId={saleOrder?.Debt?.[0]?.id || null}
      />
      <QuickViewTransactionsModal
        isOpen={isTransactionModalOpen}
        onClose={() => setIsTransactionModalOpen(false)}
        transactions={saleOrder.Transactions || []}
      />
      <ConfirmationModal
        isOpen={isModalOpenCancel}
        onCancel={() => {
          setIsModalOpenCancel(false);
        }}
        title='Xác nhận hủy đơn hàng'
        subtitle='Bạn có chắc chắn muốn hủy đơn hàng này?'
        confirmText='Hủy đơn hàng'
        cancelText='Quay lại'
        onConfirm={async () => {
          try {
            const res = await updateCancelSaleOrder({ id: saleOrderId }).unwrap();
            showAlertNotification({
              type: 'success',
              title: 'Hủy đơn hàng thành công',
              message: 'đơn hàng đã được hủy',
            });
            if (res) {
              navigate(`/management/sale-orders/detail?id=${saleOrderId}`);
            }
          } catch (error) {
            console.log(error);
            showAlertNotification({
              type: 'error',
              title: 'Hủy đơn hàng thất bại',
              message: 'Hủy đơn hàng thất bại!',
            });
          } finally {
            setIsModalOpenCancel(false);
          }
        }}
        icon='delete'
      ></ConfirmationModal>
      <AddExtraFeeModal
        saleOrderId={saleOrder?.id || ''}
        isOpen={isExtraFeeModalOpen}
        onCancel={() => setIsExtraFeeModalOpen(false)}
      />
    </div>
  );
};

export default SaleOrderDetailPayment;
